import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ExecutivePackResult } from '@whinerp/client';
import { formatCount, formatMoney, formatPercent, todayIn } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * The executive / board pack (G-214): leadership's numbers for a month,
 * quarter or year, each beside the period before, printable as it stands;
 * and against our own history - last year and the usual (G-218).
 */

const PERIODS = ['month', 'quarter', 'year'] as const;
const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function ExecutivePackPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('analytics.view');
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>('month');
  const endingOn = todayIn(format);
  const pack = useQuery<ExecutivePackResult, ApiError>({
    queryKey: ['executive-pack', period, endingOn],
    queryFn: () => api.executivePack({ query: { period, endingOn } }),
    enabled,
    placeholderData: (previous) => previous,
  });
  if (!enabled || !pack.data) return null;
  const data = pack.data;
  const show = (kind: string, value: number | null): string => {
    if (value === null) return '-';
    if (kind === 'money') return formatMoney(value.toFixed(2), format, { currency: data.currency });
    if (kind === 'ratio') return formatPercent(value, format);
    return formatCount(value, format);
  };
  return (
    <Panel data-region="executive-pack">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('execPack.title')}</SectionLabel>
          <span className="text-meta text-ink-meta" data-pack-span>{t('execPack.span', { values: { from: data.from, to: data.to, prevFrom: data.previousFrom, prevTo: data.previousTo } })}</span>
          <select className={`${INPUT} ms-auto`} data-pack-period aria-label={t('execPack.period')} value={period} onChange={(event) => setPeriod(event.target.value as 'month')}>
            {PERIODS.map((key) => <option key={key} value={key}>{t(`execPack.period.${key}`)}</option>)}
          </select>
          <Button intent="secondary" data-pack-print onClick={() => window.print()}>{t('execPack.print')}</Button>
        </div>
        <div data-scroll-container className="overflow-x-auto">
        <table className="w-full text-dense">
          <thead>
            <tr className="text-meta text-ink-meta">
              <th className="p-1 text-start font-normal" />
              <th className="p-1 text-end font-normal">{t('execPack.current')}</th>
              <th className="p-1 text-end font-normal">{t('execPack.previous')}</th>
              <th className="p-1 text-end font-normal">{t('execPack.change')}</th>
              <th className="p-1 text-end font-normal">{t('execPack.lastYear')}</th>
              <th className="p-1 text-end font-normal">{t('execPack.baseline')}</th>
              <th className="p-1 text-end font-normal">{t('execPack.againstBaseline')}</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.metric} data-pack-metric={line.metric}>
                <td className="p-1">{t(`execPack.metric.${line.metric}`)}</td>
                <td className="p-1 text-end" data-pack-current>{show(line.kind, line.current)}</td>
                <td className="p-1 text-end">{show(line.kind, line.previous)}</td>
                <td className="p-1 text-end" data-pack-change>{line.change === null ? '-' : formatPercent(line.change, format, { sign: true })}</td>
                <td className="p-1 text-end" data-pack-last-year>{show(line.kind, line.lastYear)}</td>
                <td className="p-1 text-end" data-pack-baseline>{show(line.kind, line.baseline)}</td>
                <td className="p-1 text-end" data-pack-against-baseline>{line.againstBaseline === null ? '-' : formatPercent(line.againstBaseline, format, { sign: true })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="text-meta text-ink-meta">{t('execPack.baselineNote')}</p>
        {data.marginWithheld ? <p className="text-meta text-ink-meta" data-pack-margin-withheld>{t('execPack.marginWithheld')}</p> : null}
      </div>
    </Panel>
  );
}
